/**
 * Sector roll-up: cap-weighted return, total mcap, breadth KPIs (F9, plan §3.3).
 *
 * Two shapes on purpose. `SectorAggregateDetail` is what `core` computes and
 * carries `avgVol` plus the member rows the sector screen and the treemap meta
 * line need; `toSectorAggregate` narrows it to the wire schema. The wire type is
 * change-controlled (ADR-0005), so a UI affordance that needs one more number
 * does not get to widen the contract by accident — it stays core-internal until
 * an ADR promotes it.
 *
 * Ordering is cap-weighted return DESCENDING, which is also the treemap's
 * incoming order; `squarify` re-sorts by value, so the two orders are
 * independent and neither is load-bearing for the other.
 */
import {
  SECTOR_IDS,
  type SectorAggregate,
  type SectorId,
  type WindowKey,
} from '@meridian/contracts';

import { COLLATOR } from '../constants/collation';
import { SECN } from '../constants/sectors';

import { computeBreadth } from './breadth';
import { capWeightedReturn } from './cap-weighted-return';

export interface SectorMember {
  readonly sc: SectorId;
  readonly mcap: number | null;
  readonly vol: number | null;
  readonly ret: Readonly<Record<WindowKey, number | null>>;
}

export interface SectorAggregateDetail<T extends SectorMember = SectorMember> {
  readonly sc: SectorId;
  readonly name: string;
  readonly mcap: number;
  /** `null` when no member carried weight — never coalesced to 0 (ADR-0015). */
  readonly ret: number | null;
  readonly memberCount: number;
  readonly advancers: number;
  readonly decliners: number;
  /** Mean annualised vol across members that report one. Core-only. */
  readonly avgVol: number;
  /** The member rows, in input order. Core-only. */
  readonly members: readonly T[];
}

/**
 * Eleven filters over the row set rather than one accumulating pass. The
 * accumulator version needs `bucket.push`, which is a mutation core does not
 * make; SECTOR_IDS is a closed 11-element tuple, so the cost is 11 linear scans
 * of a few hundred rows and member order is preserved for free. Sectors with no
 * members are dropped, so the result carries only represented sectors.
 */
function groupBySector<T extends SectorMember>(
  rows: readonly T[],
): readonly (readonly [SectorId, readonly T[]])[] {
  return SECTOR_IDS.map((sc) => [sc, rows.filter((r) => r.sc === sc)] as const).filter(
    ([, members]) => members.length > 0,
  );
}

function mean(values: readonly number[]): number {
  return values.length === 0 ? 0 : values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * A member missing mcap or ret is excluded from the WEIGHTING but stays a
 * member: it is still in the sector, it just cannot carry a weight (R2b).
 * Dropping it from `memberCount` too would silently redefine "breadth 4/9".
 *
 * Total `mcap` still includes known caps even when ret is missing — incomplete
 * history must not shrink the treemap tile or sector mcap KPI. Cap-weighted
 * return uses only complete (mcap ∧ ret) pairs with their own weight total.
 */
function aggregateOne<T extends SectorMember>(
  sc: SectorId,
  members: readonly T[],
  window: WindowKey,
): SectorAggregateDetail<T> {
  const mcap = members.reduce((a, m) => a + (m.mcap ?? 0), 0);
  const breadth = computeBreadth(members, window);
  return {
    sc,
    name: SECN[sc],
    mcap,
    /* ONE implementation of the cap-weighted derivation, shared with the index
       strip, and its null contract carried through VERBATIM (ADR-0015). The
       former `?? 0` here was the last thing making two panels on one screen
       disagree about the same missing data: the index strip rendered an
       em-dash, the treemap rendered +0.0% in flat-green. */
    ret: capWeightedReturn(members, window),
    memberCount: members.length,
    advancers: breadth.advancers,
    decliners: breadth.decliners,
    avgVol: mean(members.map((m) => m.vol).filter((v): v is number => v !== null)),
    members,
  };
}

/** Descending by return, with an UNKNOWN return last in both directions. */
function compareRetDesc(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
}

export function aggregateSectors<T extends SectorMember>(
  rows: readonly T[],
  window: WindowKey,
): readonly SectorAggregateDetail<T>[] {
  return (
    groupBySector(rows)
      .map(([sc, members]) => aggregateOne(sc, members, window))
      /* Sector id tie-break: total order independent of input row order.
         An unknown return sorts LAST rather than as 0 — `b.ret - a.ret` over a
         null yields NaN, and a NaN comparator silently returns whatever order
         the engine's sort happened to visit (ADR-0015). */
      .sort((a, b) => compareRetDesc(a.ret, b.ret) || COLLATOR.compare(a.sc, b.sc))
  );
}

/** Narrow the core detail to the change-controlled wire shape. */
export function toSectorAggregate(detail: SectorAggregateDetail): SectorAggregate {
  return {
    sc: detail.sc,
    name: detail.name,
    mcap: detail.mcap,
    ret: detail.ret,
    memberCount: detail.memberCount,
    advancers: detail.advancers,
    decliners: detail.decliners,
  };
}
